/**
 * The one place picker (SA-1051 spec §8). Typing writes an unresolved place
 * with the text's own code — the server resolves at save, so a typed code
 * needs no click; from two characters the search offers up to five IATA
 * rows and a chosen offer is resolved from the row; a choice (from the
 * extractor or a saved value) shows its candidates as buttons; an unknown
 * code shows one sentence; a failed search says nothing. Mounted only by
 * `FieldInput` for `type === 'place'`, so it is the one React Query consumer
 * the intake forms ever mount.
 *
 * SA-1052: on a field that declares alternatives, a single station (resolved, or a typed code) and the alternatives offer "Add an alternative" (a second search), each alternative a station with "Remove <code>"; one left is a single place again.
 *
 * The box keeps its own text. `reflects` is the value key the box currently
 * stands for — what this input last wrote, or the last value it took from
 * outside (an extraction, a loaded record) — so the echo of its own write never
 * resets the text under the caret (a cleared box must not refill), while a
 * value that arrives from outside always does, even after an edit that left
 * the value's key unchanged (a trailing space).
 */
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { useAirportSearch } from '@/hooks/useAirports'
import { Station } from '@/components/records/Station'
import {
  ALTERNATIVES_MAX, OR, alternativeEntries, candidateChosen, chosenPlace, countryName, entryStationWords, isPlace, placeCodes, placeKey,
  placeState, placeStationWords, typedPlace, withAlternative, withoutAlternative,
  type PlaceCandidate, type PlaceValue,
} from '@/components/records/place'
import { ADD_ALTERNATIVE, ANOTHER_ALTERNATIVE, CANCEL_ALTERNATIVE, alternativesText, unknownPlaceText } from '@/components/records/placeText'
import { stationCode } from '@/components/records/stationCode'
import type { Airport } from '@/types/airports'

const shown = (value: unknown): string => {
  if (isPlace(value)) return value.resolved && value.code ? value.code : value.text
  return typeof value === 'string' ? value : ''
}
const offerLabel = (a: Airport): string => `${a.iata ?? a.icao ?? a.ident} · ${a.displayName}`
const candidateLabel = (c: PlaceCandidate): string => (c.name ? `${c.code} · ${c.name}` : c.code)
/** `City, Country` with the country in words; a missing part leaves no dangling comma (spec §8.1). */
const candidatePlace = (c: PlaceCandidate): string => [c.city, countryName(c.country)].filter(Boolean).join(', ')
/** The candidates worth a button: a list only, one per code (the heading counts them the same way, `placeCodes`). */
function listedCandidates(place: PlaceValue): PlaceCandidate[] {
  const out: PlaceCandidate[] = []
  for (const c of Array.isArray(place.candidates) ? place.candidates : []) {
    if (c && typeof c.code === 'string' && c.code && !out.some((o) => o.code === c.code)) out.push(c)
  }
  return out
}

export function PlaceInput({ id, value, onChange, describedBy, allowAlternatives = false }: {
  id: string
  value: unknown
  onChange: (v: PlaceValue | null) => void
  describedBy?: string
  allowAlternatives?: boolean
}) {
  const key = placeKey(value)
  const [typed, setTyped] = useState(() => shown(value))
  const reflects = useRef(key)
  useEffect(() => {
    if (reflects.current === key) return
    reflects.current = key
    setTyped(shown(value))
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  const place = isPlace(value) ? value : null
  const state = place ? placeState(place) : null
  const settledState = state === 'resolved' || state === 'choice' || state === 'alternatives'
  const typedCode = stationCode(typed)
  const search = useAirportSearch(typedCode ?? typed, { iataOnly: true, enabled: !settledState })
  const settled = search.asked === (typedCode ?? typed).trim()
  const offers = settledState || !settled ? [] : search.data ?? []
  const unknownCode = state === 'unknown' && settled && search.isSuccess && !search.data?.some((a) => a.iata === place?.code) ? place?.code ?? null : null

  // SA-1052: the second search, for an alternative.
  const [adding, setAdding] = useState(false)
  const [altTyped, setAltTyped] = useState('')
  const held = place ? placeCodes(place) : []
  const canAdd = allowAlternatives && place !== null
    && (state === 'resolved' || state === 'unknown' || state === 'alternatives') && held.length < ALTERNATIVES_MAX
  const altCode = stationCode(altTyped)
  const altSearch = useAirportSearch(altCode ?? altTyped, { iataOnly: true, enabled: adding && canAdd })
  const altSettled = altSearch.asked === (altCode ?? altTyped).trim()
  const altOffers = adding && altSettled ? (altSearch.data ?? []).filter((a) => a.iata && !held.includes(a.iata)) : []

  // The Add panel goes away with the right to add (a pick that settles the place, a removal, a new value from outside).
  useEffect(() => { if (!canAdd) setAdding(false) }, [canAdd])
  // Focus: the panel's input when it opens; after a pick or a removal, a control that still exists.
  const mainRef = useRef<HTMLInputElement>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  const altRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (adding) altRef.current?.focus() }, [adding])
  // After a pick or a removal, focus a control that still exists. A counter, not a flag:
  // it is set in the same handler as the write, so the effect runs once, after the render
  // that shows the new value (and the Add button, when adding is still allowed).
  const [refocusTick, setRefocusTick] = useState(0)
  useEffect(() => {
    if (refocusTick === 0) return
    ;(addRef.current ?? mainRef.current)?.focus()
  }, [refocusTick])

  const write = (next: PlaceValue | null) => { reflects.current = placeKey(next); onChange(next) }
  const type = (text: string) => { setTyped(text); write(typedPlace(text)) }
  const choose = (a: Airport) => { setTyped(a.iata ?? ''); write(chosenPlace(typed, a)) }
  const pick = (p: PlaceValue, c: PlaceCandidate) => { setTyped(c.code); write(candidateChosen(p, c)) }
  const addAlternative = (p: PlaceValue, a: Airport) => {
    const next = withAlternative(p, a)
    setRefocusTick((n) => n + 1)
    setTyped(shown(next)); write(next); setAdding(false); setAltTyped('')
  }
  const removeAlternative = (p: PlaceValue, code: string) => {
    const next = withoutAlternative(p, code)
    setRefocusTick((n) => n + 1)
    setTyped(shown(next)); write(next)
  }

  return (
    <div className="space-y-1.5">
      <Input id={id} ref={mainRef} value={typed} aria-describedby={describedBy} autoComplete="off" onChange={(e) => type(e.target.value)} />
      {place && state === 'resolved' && <Station text={place.code ?? ''} airport={placeStationWords(place)} />}
      {place && state === 'alternatives' && (
        <div className="space-y-1">
          <p className="text-sm">{alternativesText(placeCodes(place))}</p>
          <ul aria-label="The customer's alternatives" className="flex flex-wrap gap-1.5">
            {alternativeEntries(place).map((c) => (
              <li key={c.code} className="inline-flex items-center gap-1">
                <Station text={c.code} airport={entryStationWords(c)} />
                <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${c.code}`}
                  className="h-6 px-1.5 text-muted-foreground hover:text-destructive"
                  onClick={() => removeAlternative(place, c.code)}>
                  <X className="size-3.5" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {place && state === 'choice' && (
        <div className="space-y-1">
          <p className="text-sm">{`${place.text}: ${placeCodes(place).join(OR)}?`}</p>
          <ul aria-label="Which airport" className="flex flex-wrap gap-1.5">
            {listedCandidates(place).map((c) => (
              <li key={c.code}>
                <Button type="button" size="sm" variant="outline" onClick={() => pick(place, c)}>
                  <span className="font-mono font-semibold">{candidateLabel(c)}</span>
                  {candidatePlace(c) && <>{' '}<span className="text-xs text-muted-foreground">— {candidatePlace(c)}</span></>}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {offers.length > 0 && (
        <ul aria-label="Matching airports" className="flex flex-wrap gap-1.5">
          {offers.map((a) => (
            <li key={a.ident}>
              <Button type="button" size="sm" variant="outline" onClick={() => choose(a)}>
                <span className="font-mono font-semibold">{offerLabel(a)}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">{a.placeWords}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {unknownCode && <p role="status" className="text-sm text-destructive">{unknownPlaceText(unknownCode)}</p>}
      {canAdd && place && !adding && (
        <Button ref={addRef} type="button" size="sm" variant="outline" onClick={() => { setAdding(true); setAltTyped('') }}>{ADD_ALTERNATIVE}</Button>
      )}
      {canAdd && place && adding && (
        <div className="space-y-1.5 rounded-lg border border-border p-2">
          <label htmlFor={`${id}-alternative`} className="text-sm">{ANOTHER_ALTERNATIVE}</label>
          <Input id={`${id}-alternative`} ref={altRef} value={altTyped} autoComplete="off" onChange={(e) => setAltTyped(e.target.value)} />
          {altOffers.length > 0 && (
            <ul aria-label="Airports for the alternative" className="flex flex-wrap gap-1.5">
              {altOffers.map((a) => (
                <li key={a.ident}>
                  <Button type="button" size="sm" variant="outline" onClick={() => addAlternative(place, a)}>
                    <span className="font-mono font-semibold">{offerLabel(a)}</span>
                    <span className="ml-1.5 text-xs text-muted-foreground">{a.placeWords}</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>{CANCEL_ALTERNATIVE}</Button>
        </div>
      )}
    </div>
  )
}
